<template>
  <van-dialog
    v-model:show="showDialog"
    :title="isForce ? '需要更新' : '发现新版本'"
    :show-confirm-button="true"
    :show-cancel-button="!isForce"
    :close-on-click-overlay="!isForce"
    confirm-button-text="立即更新"
    cancel-button-text="稍后再说"
    @confirm="handleUpdate"
    @cancel="handleCancel"
    class="update-dialog"
  >
    <div class="update-content">
      <div class="version-info">
        <div class="current-version">当前版本：{{ currentVersion }}</div>
        <div class="latest-version">最新版本：{{ versionInfo.version }}</div>
      </div>

      <div class="update-notes" v-if="versionInfo.releaseNotes">
        <div class="notes-title">更新内容：</div>
        <div class="notes-content">{{ versionInfo.releaseNotes }}</div>
      </div>

      <div class="update-tip" v-if="isForce">
        <van-icon name="warning-o" color="#ff6034" />
        <span>此版本为重要更新，必须更新后才能继续使用</span>
      </div>
    </div>
  </van-dialog>
</template>

<script setup>
import { ref, computed } from 'vue'
import { showToast } from 'vant'
import { Browser } from '@capacitor/browser'
import { openStoreApp, getStoreUrl } from '@/utils/device'
import { Capacitor } from '@capacitor/core'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  versionInfo: {
    type: Object,
    default: () => ({})
  },
  currentVersion: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:show', 'update'])

const showDialog = computed({
  get: () => props.show,
  set: (val) => {
    // 强制更新不允许关闭弹窗
    if (isForce.value && val === false) {
      console.log('⚠️ 强制更新，不允许关闭弹窗')
      return
    }
    emit('update:show', val)
  }
})

const isForce = computed(() => props.versionInfo.isForce === 1)

// 立即更新
const handleUpdate = async () => {
  console.log('🔗 处理更新点击')

  // iOS 平台使用 SystemPlugin 打开（避免 WebView 无法处理 itms-apps:// 协议）
  if (window.Capacitor && window.Capacitor.getPlatform() === 'ios') {
    const updateUrl = props.versionInfo.updateUrl
    if (!updateUrl) {
      showToast('更新链接未配置')
      return
    }
    console.log('📱 iOS 平台，使用 SystemPlugin.openSystemUrl 跳转:', updateUrl)
    try {
      const { SystemPlugin } = await import('@/plugins/system')
      await SystemPlugin.openSystemUrl({ url: updateUrl })
      console.log('✅ 已打开 App Store')
      return
    } catch (error) {
      console.error('❌ SystemPlugin.openSystemUrl 失败：', error)
    }
  }

  // Android 平台跳转应用商店
  if (window.Capacitor && window.Capacitor.getPlatform() === 'android') {
    const packageName = 'com.kenzom.aroma'

    // 优先用深链 scheme 直接拉起手机上的商店 App 并命中详情页
    // 华为用 AppGallery 专有 scheme appmarket://，其余机型 market:// 由系统路由到自带商店
    try {
      if (await openStoreApp(packageName)) return
    } catch (e) {
      console.log('⚠️ 深链流程异常:', e)
    }

    // 兜底：商店网页版（手机上没有可响应深链的商店时）
    const storeUrl = getStoreUrl(packageName)
    console.log('📱 跳转应用商店网页版兜底:', storeUrl)

    try {
      await Browser.open({ url: storeUrl })
      console.log('✅ 已打开应用商店网页版')
      return
    } catch (e) {
      console.log('⚠️ 打开应用商店失败:', e)
      showToast('请手动前往应用商店搜索"春信精油"进行更新')
    }
    return
  }

  // Web 环境用 window.open 打开
  const updateUrl = props.versionInfo.updateUrl
  if (updateUrl) {
    window.open(updateUrl, '_blank')
  } else {
    showToast('更新链接未配置')
  }
}

// 稍后再说（非强制更新）
const handleCancel = () => {
  // 强制更新不允许取消
  if (isForce.value) {
    showToast('此版本必须更新后才能继续使用')
    return
  }
  showDialog.value = false
}
</script>

<style scoped>
.update-dialog {
  --van-dialog-border-radius: 16px;
  --van-dialog-overlay-opacity: 0.3;
  width: 280px; /* 固定宽度，缩小约 1/3 */
  max-width: 85%; /* 小屏幕自适应 */
}

.update-content {
  padding: 16px; /* 恢复合理的内边距 */
}

.version-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  padding: 12px;
  background-color: #f7f8fa;
  border-radius: 8px;
}

.current-version,
.latest-version {
  font-size: 13px;
  color: #333;
}

.latest-version {
  color: #1989fa;
  font-weight: 500;
}

.update-notes {
  margin-bottom: 12px;
}

.notes-title {
  font-size: 13px;
  font-weight: 500;
  color: #333;
  margin-bottom: 6px;
}

.notes-content {
  font-size: 12px;
  color: #666;
  line-height: 1.5;
  white-space: pre-line;
  padding: 12px;
  background-color: #f7f8fa;
  border-radius: 8px;
  max-height: 120px;
  overflow-y: auto;
}

.update-tip {
  display: flex;
  align-items: center;
  padding: 10px;
  background-color: #fff8f8;
  border-radius: 8px;
  font-size: 12px;
  color: #ff6034;
}

.update-tip .van-icon {
  margin-right: 6px;
  font-size: 16px;
}
</style>
